import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  DatePicker,
  Divider,
  Modal,
  Select,
  Skeleton,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { get } from "service/api";
import moneyFormat from "functions/utils/moneyFormat";

const fmt = (v) => (v !== undefined && v !== null ? `${moneyFormat(v)}₮` : "0₮");

const StatCard = ({ title, value, color, icon, loading, sub }) => (
  <Card
    size="small"
    style={{ borderTop: `3px solid ${color}`, flex: 1, minWidth: 140 }}
    styles={{ body: { padding: "12px 16px" } }}
  >
    {loading ? (
      <Skeleton active paragraph={{ rows: 1 }} title={false} />
    ) : (
      <div className="flex items-center gap-3">
        <div style={{ color, fontSize: 22 }}>{icon}</div>
        <div>
          <div className="text-xs text-gray-400">{title}</div>
          <div className="font-bold text-sm" style={{ color }}>{value}</div>
          {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </div>
      </div>
    )}
  </Card>
);

// ─── POS Тэнцэл задаргаа ──────────────────────────────────────────────────────
const ReconciliationDetail = ({ summary, loading }) => {
  if (loading) return <Skeleton active paragraph={{ rows: 3 }} className="mb-4" />;
  if (!summary) return null;

  const posCheck = summary.reconciliation?.posCheck;
  const bank = summary.bank;
  const sys = summary.system;
  const posBalanced = posCheck?.isBalanced;
  const posDiff = parseFloat(posCheck?.diff || 0);

  const Row = ({ label, value, color, bold }) => (
    <div className="flex flex-col items-center px-4">
      <div className="text-xs text-gray-400 mb-1 whitespace-nowrap">{label}</div>
      <div
        className={bold ? "font-bold text-sm" : "text-sm"}
        style={{ color: color || "#111" }}
      >
        {value}
      </div>
    </div>
  );

  return (
    <Card
      size="small"
      className="mb-4"
      styles={{ body: { padding: "12px 16px" } }}
      title={
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold">POS Тэнцэл шалгалт</span>
          {posCheck && (
            <Tag color={posBalanced ? "success" : "error"} className="ml-1">
              {posBalanced ? "Тэнцсэн ✓" : `Зөрүү: ${fmt(Math.abs(posDiff))}`}
            </Tag>
          )}
          {posCheck?.note && (
            <span className="text-xs text-gray-400 font-normal">{posCheck.note}</span>
          )}
        </div>
      }
    >
      {/* POS Check row */}
      <div className="flex items-center flex-wrap gap-0 divide-x divide-gray-200 mb-3">
        <Row
          label={`Банкнаас орсон (${summary.settleDate})`}
          value={fmt(posCheck?.bankIncoming)}
          color="#2563eb"
          bold
        />
        <Row
          label="Систем POS settle (D)"
          value={fmt(posCheck?.systemPosSettle)}
          color="#374151"
          bold
        />
        <Row
          label="Зөрүү"
          value={fmt(posCheck?.diff)}
          color={posBalanced ? "#16a34a" : "#dc2626"}
          bold
        />
        <div className="px-4 flex flex-col items-center">
          <div className="text-xs text-gray-400 mb-1">Статус</div>
          {posBalanced ? (
            <CheckCircleOutlined style={{ color: "#16a34a", fontSize: 20 }} />
          ) : (
            <CloseCircleOutlined style={{ color: "#dc2626", fontSize: 20 }} />
          )}
        </div>
      </div>

      <Divider className="my-2" />

      {/* Outgoing breakdown + current day settle */}
      <div className="flex flex-wrap gap-0 divide-x divide-gray-200">
        <Row
          label="Гарсан: POS дүн"
          value={fmt(bank?.outgoingBreakdown?.pos)}
          color="#7c3aed"
        />
        <Row
          label="Гарсан: QPAY дүн"
          value={fmt(bank?.outgoingBreakdown?.qpay)}
          color="#0369a1"
        />
        <div className="w-px" />
        <Row
          label={`Систем D POS settle`}
          value={fmt(sys?.posSettleTotal)}
          color="#374151"
        />
        <Row
          label="Систем D QPAY settle"
          value={fmt(sys?.qpaySettleTotal)}
          color="#374151"
        />
      </div>

      {/* D-1 info */}
      {sys?.prevDate && (
        <div className="mt-2 pt-2 border-t text-xs text-gray-400 flex gap-4 flex-wrap">
          <span className="font-medium text-gray-500">D-1 ({sys.prevDate.date}):</span>
          <span>POS {fmt(sys.prevDate.posSettleTotal)}</span>
          <span>QPAY {fmt(sys.prevDate.qpaySettleTotal)}</span>
        </div>
      )}
    </Card>
  );
};

// ─── Tab 1: Bank Log ───────────────────────────────────────────────────────────
const BankLogTab = ({ date, account }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!date || !account) return;
    setLoading(true);
    const res = await get(`/reconciliation/bank-log?date=${date}&account=${account}`);
    const d = res?.data || null;
    if (d) {
      d.incoming = Array.isArray(d.incoming) ? d.incoming : [];
      d.outgoing = Array.isArray(d.outgoing) ? d.outgoing : [];
      d.other = Array.isArray(d.other) ? d.other : [];
    }
    setData(d);
    setLoading(false);
  }, [date, account]);

  useEffect(() => { load(); }, [load]);

  const cols = (sign) => [
    { title: "ID", dataIndex: "id", key: "id", width: 80, className: "text-xs" },
    {
      title: "Тайлбар",
      dataIndex: "description",
      key: "description",
      className: "text-xs",
      render: (v) => {
        const match = v?.match(/#(\d+)#/);
        if (match) {
          return (
            <span>
              {v.replace(match[0], "")}{" "}
              <Tag color="blue" className="text-xs">#{match[1]}#</Tag>
            </span>
          );
        }
        return <span>{v}</span>;
      },
    },
    {
      title: "Дүн",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      className: "text-xs",
      render: (v) => (
        <span style={{ color: sign > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
          {fmt(Math.abs(parseFloat(v)))}
        </span>
      ),
    },
    {
      title: "Огноо",
      dataIndex: "trnDate",
      key: "trnDate",
      width: 110,
      className: "text-xs",
      render: (v) => v ? dayjs(v).format("YYYY-MM-DD") : "-",
    },
  ];

  if (loading) return <Skeleton active />;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="font-semibold text-green-700 mb-2 flex items-center gap-2">
          <BankOutlined /> Орж ирсэн (Банкнаас — POS Settlement)
          <Tag color="green">{data.summary?.incomingCount} тоо</Tag>
          <Tag color="green">{fmt(data.summary?.totalIncoming)}</Tag>
        </div>
        <Table
          size="small"
          columns={cols(1)}
          dataSource={data.incoming || []}
          rowKey="id"
          pagination={false}
          bordered
        />
      </div>
      <div>
        <div className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
          <BankOutlined /> Гарсан (Манайх хийсэн)
          <Tag color="blue">{data.summary?.outgoingCount} тоо</Tag>
          <Tag color="blue">{fmt(data.summary?.totalOutgoing)}</Tag>
        </div>
        <Table
          size="small"
          columns={cols(-1)}
          dataSource={data.outgoing || []}
          rowKey="id"
          pagination={false}
          bordered
        />
      </div>
    </div>
  );
};

// ─── Tab 2: MerchantSettle ────────────────────────────────────────────────────
const MerchantSettleTab = ({ date }) => {
  const [data, setData] = useState([]);
  const [settleSummary, setSettleSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failedModal, setFailedModal] = useState(null);
  const [failedData, setFailedData] = useState(null);
  const [failedLoading, setFailedLoading] = useState(false);

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    const res = await get(`/reconciliation/merchant-settle?date=${date}`);
    const settles = Array.isArray(res?.data?.settles) ? res.data.settles : [];
    setData(settles);
    setSettleSummary(res?.data?.summary || null);
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const openFailed = async (record) => {
    setFailedModal(record);
    setFailedLoading(true);
    const res = await get(`/reconciliation/failed-transactions?merchantSettleId=${record.id}`);
    const fd = res?.data || null;
    if (fd) fd.transactions = Array.isArray(fd.transactions) ? fd.transactions : [];
    setFailedData(fd);
    setFailedLoading(false);
  };

  const cols = [
    { title: "ID", dataIndex: "id", key: "id", width: 70, className: "text-xs" },
    { title: "Merchant ID", dataIndex: "merchantId", key: "merchantId", width: 90, className: "text-xs" },
    {
      title: "Settle дүн",
      dataIndex: "settleAmount",
      key: "settleAmount",
      className: "text-xs",
      render: (v) => <span className="font-semibold">{fmt(v)}</span>,
    },
    {
      title: "Settled тоо",
      key: "settledCount",
      className: "text-xs",
      render: (_, r) => r.transactions?.settledCount ?? "-",
    },
    {
      title: "Failed тоо",
      key: "failedCount",
      className: "text-xs",
      render: (_, r) => (
        <Tag color={r.transactions?.failedCount > 0 ? "red" : "default"}>
          {r.transactions?.failedCount ?? 0}
        </Tag>
      ),
    },
    {
      title: "Settled дүн",
      key: "settledAmount",
      className: "text-xs",
      render: (_, r) => fmt(r.transactions?.settledAmount),
    },
    {
      title: "Failed дүн",
      key: "failedAmount",
      className: "text-xs",
      render: (_, r) => (
        <span style={{ color: r.transactions?.failedCount > 0 ? "#dc2626" : undefined }}>
          {fmt(r.transactions?.failedAmount)}
        </span>
      ),
    },
    {
      title: "Зөрүү",
      dataIndex: "difference",
      key: "difference",
      className: "text-xs",
      render: (v) => (
        <span style={{ color: parseFloat(v) !== 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
          {fmt(v)}
        </span>
      ),
    },
    {
      title: "",
      key: "action",
      width: 80,
      render: (_, r) =>
        r.hasProblem ? (
          <Button size="small" danger onClick={() => openFailed(r)}>FAILED</Button>
        ) : (
          <Button size="small" onClick={() => openFailed(r)}>Харах</Button>
        ),
    },
  ];

  const failedCols = [
    { title: "ID", dataIndex: "id", key: "id", width: 70, className: "text-xs" },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: 90,
      className: "text-xs",
      render: (v) => <Tag color="red">{v}</Tag>,
    },
    { title: "Данс", dataIndex: "accountNumber", key: "accountNumber", className: "text-xs" },
    { title: "Дүн", dataIndex: "amount", key: "amount", className: "text-xs", render: (v) => fmt(v) },
    {
      title: "Шимтгэлийн дараа",
      dataIndex: "afterFeeAmount",
      key: "afterFeeAmount",
      className: "text-xs",
      render: (v) => fmt(v),
    },
  ];

  return (
    <>
      {settleSummary && (
        <div className="mb-3 flex gap-3 flex-wrap">
          <Tag color="blue">Нийт settle: {settleSummary.settleCount}</Tag>
          <Tag color="blue">Нийт дүн: {fmt(settleSummary.totalSettleAmount)}</Tag>
          <Tag color={parseFloat(settleSummary.totalFailedAmount) > 0 ? "red" : "default"}>
            FAILED дүн: {fmt(settleSummary.totalFailedAmount)}
          </Tag>
          <Tag color={settleSummary.problemCount > 0 ? "red" : "green"}>
            Асуудалтай: {settleSummary.problemCount}
          </Tag>
        </div>
      )}
      <Table
        size="small"
        columns={cols}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        bordered
        rowClassName={(r) => (r.hasProblem ? "bg-red-50" : "")}
      />
      <Modal
        open={!!failedModal}
        onCancel={() => { setFailedModal(null); setFailedData(null); }}
        footer={null}
        title={`Merchant #${failedModal?.merchantId} — FAILED гүйлгээнүүд`}
        width={700}
      >
        {failedLoading ? (
          <Skeleton active />
        ) : (
          <>
            {failedData && (
              <div className="mb-3 flex gap-4">
                <Tag color="red">Тоо: {failedData.count}</Tag>
                <Tag color="red">Нийт дүн: {fmt(failedData.totalAmount)}</Tag>
              </div>
            )}
            <Table
              size="small"
              columns={failedCols}
              dataSource={failedData?.transactions || []}
              rowKey="id"
              pagination={false}
              bordered
            />
          </>
        )}
      </Modal>
    </>
  );
};

// ─── Tab 3: Failed Transactions ───────────────────────────────────────────────
const FailedTab = ({ date }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    const res = await get(`/reconciliation/failed-transactions?date=${date}`);
    const fd = res?.data || null;
    if (fd) fd.transactions = Array.isArray(fd.transactions) ? fd.transactions : [];
    setData(fd);
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { title: "ID", dataIndex: "id", key: "id", width: 70, className: "text-xs" },
    { title: "Settle ID", dataIndex: "merchantSettleId", key: "merchantSettleId", width: 90, className: "text-xs" },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: 90,
      className: "text-xs",
      render: (v) => <Tag color="red">{v}</Tag>,
    },
    { title: "Данс", dataIndex: "accountNumber", key: "accountNumber", className: "text-xs" },
    { title: "Дүн", dataIndex: "amount", key: "amount", className: "text-xs", render: (v) => fmt(v) },
    {
      title: "Шимтгэлийн дараа",
      dataIndex: "afterFeeAmount",
      key: "afterFeeAmount",
      className: "text-xs",
      render: (v) => fmt(v),
    },
    {
      title: "Огноо",
      dataIndex: "updatedAt",
      key: "updatedAt",
      className: "text-xs",
      render: (v) => v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  if (loading) return <Skeleton active />;

  return (
    <div>
      {data && (
        <div className="mb-3 flex gap-4">
          <Tag color="red" className="text-sm">Нийт тоо: {data.count}</Tag>
          <Tag color="red" className="text-sm">Нийт дүн: {fmt(data.totalAmount)}</Tag>
        </div>
      )}
      <Table
        size="small"
        columns={cols}
        dataSource={data?.transactions || []}
        rowKey="id"
        pagination={{ pageSize: 20 }}
        bordered
      />
    </div>
  );
};

// ─── Tab 4: Buckets ───────────────────────────────────────────────────────────
const BucketsTab = ({ date }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    const res = await get(`/reconciliation/buckets?date=${date}`);
    const bd = res?.data || null;
    if (bd) bd.byAccount = Array.isArray(bd.byAccount) ? bd.byAccount : [];
    setData(bd);
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const summaryCols = [
    { title: "Данс", dataIndex: "fromAccountNumber", key: "fromAccountNumber", className: "text-xs" },
    { title: "Тоо", dataIndex: "count", key: "count", width: 70, className: "text-xs" },
    {
      title: "Нийт дүн",
      dataIndex: "totalAmount",
      key: "totalAmount",
      className: "text-xs",
      render: (v) => fmt(v),
    },
    {
      title: "Нийт шимтгэл",
      dataIndex: "totalFee",
      key: "totalFee",
      className: "text-xs",
      render: (v) => fmt(v),
    },
  ];

  if (loading) return <Skeleton active />;

  return (
    <div>
      {data?.summary && (
        <div className="mb-3 flex gap-4">
          <Tag color="blue" className="text-sm">Bucket тоо: {data.summary.bucketCount}</Tag>
          <Tag color="blue" className="text-sm">Нийт: {fmt(data.summary.grandTotal)}</Tag>
          <Tag color="blue" className="text-sm">Шимтгэл: {fmt(data.summary.grandFee)}</Tag>
        </div>
      )}
      <Table
        size="small"
        columns={summaryCols}
        dataSource={data?.byAccount || []}
        rowKey="fromAccountNumber"
        pagination={false}
        bordered
        title={() => <span className="font-semibold">Дансаар бүлэглэсэн</span>}
      />
    </div>
  );
};

// ─── Tab 5: Trend ─────────────────────────────────────────────────────────────
const TrendTab = ({ account }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    const res = await get(`/reconciliation/trend?days=7&account=${account}`);
    const trend = res?.data?.trend;
    setData(Array.isArray(trend) ? trend : []);
    setLoading(false);
  }, [account]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { title: "Огноо", dataIndex: "date", key: "date", className: "text-xs font-semibold" },
    {
      title: "Банкнаас орсон",
      dataIndex: "incoming",
      key: "incoming",
      className: "text-xs",
      render: (v) => <span className="text-blue-600 font-semibold">{fmt(v)}</span>,
    },
    {
      title: "Манайх гарсан",
      dataIndex: "outgoing",
      key: "outgoing",
      className: "text-xs",
      render: (v) => <span className="text-green-600 font-semibold">{fmt(v)}</span>,
    },
    {
      title: "xPay хадгалсан",
      dataIndex: "netKept",
      key: "netKept",
      className: "text-xs",
      render: (v) => <span className="text-emerald-700 font-bold">{fmt(v)}</span>,
    },
    { title: "Орж ирсэн тоо", dataIndex: "incomingCount", key: "incomingCount", width: 110, className: "text-xs" },
    { title: "Гарсан тоо", dataIndex: "outgoingCount", key: "outgoingCount", width: 100, className: "text-xs" },
  ];

  if (loading) return <Skeleton active />;

  return (
    <Table
      size="small"
      columns={cols}
      dataSource={data}
      rowKey="date"
      pagination={false}
      bordered
    />
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Reconciliation = () => {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState("5570025382");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAccounts = async () => {
    const res = await get("/reconciliation/accounts");
    if (res?.data) {
      setAccounts(res.data);
      if (res.data.length > 0 && !account) setAccount(res.data[0].accountNumber);
    }
  };

  const loadSummary = useCallback(async () => {
    if (!date || !account) return;
    setSummaryLoading(true);
    setError(null);
    const res = await get(`/reconciliation/summary?settle_date=${date}&account=${account}`);
    if (res?.data) {
      setSummary(res.data);
    } else {
      setError("Мэдээлэл ачааллахад алдаа гарлаа.");
    }
    setSummaryLoading(false);
  }, [date, account]);

  useEffect(() => { loadAccounts(); }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const isBalanced = summary?.reconciliation?.isBalanced;
  const posBalanced = summary?.reconciliation?.posCheck?.isBalanced;
  const failedCount = summary?.reconciliation?.failedTransactionCount || 0;
  const bankVsSystemDiff = parseFloat(summary?.reconciliation?.bankVsSystemDiff || 0);

  const tabItems = [
    {
      key: "bankLog",
      label: "Банкны лог",
      children: <BankLogTab date={date} account={account} />,
    },
    {
      key: "merchantSettle",
      label: "Settle задаргаа",
      children: <MerchantSettleTab date={date} />,
    },
    {
      key: "failed",
      label: (
        <Badge count={failedCount} size="small" offset={[6, 0]}>
          FAILED
        </Badge>
      ),
      children: <FailedTab date={date} />,
    },
    {
      key: "buckets",
      label: "Bucket",
      children: <BucketsTab date={date} />,
    },
    {
      key: "trend",
      label: "7 хоногийн трэнд",
      children: <TrendTab account={account} />,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      {/* Header controls */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-gray-500 text-sm">Данс:</span>
          <Select
            value={account}
            onChange={setAccount}
            style={{ width: 160 }}
            size="small"
            options={
              accounts.length > 0
                ? accounts.map((a) => ({ value: a.accountNumber, label: a.accountNumber }))
                : [{ value: "5570025382", label: "5570025382" }]
            }
          />
        </div>
        <div className="flex items-center gap-2">
          <DatePicker
            value={dayjs(date)}
            onChange={(d) => d && setDate(d.format("YYYY-MM-DD"))}
            format="YYYY-MM-DD"
            size="small"
            allowClear={false}
          />
          <Tooltip title="Шинэчлэх">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={loadSummary}
              loading={summaryLoading}
            />
          </Tooltip>
        </div>
      </div>

      {/* Alerts */}
      {summary && isBalanced === false && (
        <Alert
          message={`Банк vs Систем зөрүү: ${fmt(Math.abs(bankVsSystemDiff))}`}
          type="error"
          showIcon
          icon={<WarningOutlined />}
          className="mb-2"
          banner
        />
      )}
      {summary && posBalanced === false && (
        <Alert
          message={`POS тэнцэл зөрүү: ${fmt(Math.abs(parseFloat(summary.reconciliation?.posCheck?.diff || 0)))}`}
          description="Банкнаас орсон POS settlement болон системийн POS settle дүн зөрж байна."
          type="warning"
          showIcon
          className="mb-2"
        />
      )}
      {failedCount > 0 && (
        <Alert
          message={`FAILED гүйлгээ: ${failedCount} тоо`}
          type="warning"
          showIcon
          className="mb-2"
        />
      )}
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          action={<Button size="small" onClick={loadSummary}>Дахин оролдох</Button>}
          className="mb-2"
        />
      )}

      {/* Stat cards */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <StatCard
          title="Банкнаас орсон"
          value={fmt(summary?.bank?.incoming)}
          color="#2563eb"
          icon={<BankOutlined />}
          loading={summaryLoading}
          sub="POS Settlement"
        />
        <StatCard
          title="Манайх гарсан"
          value={fmt(summary?.bank?.outgoing)}
          color="#16a34a"
          icon={<BankOutlined />}
          loading={summaryLoading}
          sub={
            summary?.bank?.outgoingBreakdown
              ? `POS ${fmt(summary.bank.outgoingBreakdown.pos)} · QPAY ${fmt(summary.bank.outgoingBreakdown.qpay)}`
              : undefined
          }
        />
        <StatCard
          title="POS нэт (орсон − гарсан)"
          value={fmt(
            summary
              ? (
                  parseFloat(summary.bank?.incoming || 0) -
                  parseFloat(summary.bank?.outgoingBreakdown?.pos || 0)
                ).toFixed(2)
              : 0
          )}
          color="#059669"
          icon={<CheckCircleOutlined />}
          loading={summaryLoading}
          sub="bank.incoming − outgoing.pos"
        />
        <StatCard
          title="POS тэнцэл"
          value={
            summary
              ? posBalanced
                ? "Тэнцсэн ✓"
                : `Зөрүү ${fmt(Math.abs(parseFloat(summary.reconciliation?.posCheck?.diff || 0)))}`
              : "—"
          }
          color={
            summary
              ? posBalanced
                ? "#16a34a"
                : "#f59e0b"
              : "#9ca3af"
          }
          icon={posBalanced ? <CheckCircleOutlined /> : <WarningOutlined />}
          loading={summaryLoading}
        />
        <StatCard
          title="FAILED тоо / дүн"
          value={failedCount > 0 ? `${failedCount} тоо` : "0"}
          color={failedCount > 0 ? "#dc2626" : "#9ca3af"}
          icon={<WarningOutlined />}
          loading={summaryLoading}
          sub={failedCount > 0 ? fmt(summary?.reconciliation?.failedTransactionAmount) : undefined}
        />
        <StatCard
          title="Банк vs Систем"
          value={
            summary
              ? isBalanced
                ? "Тэнцсэн ✓"
                : "Зөрүүтэй ✗"
              : "—"
          }
          color={
            summary
              ? isBalanced
                ? "#16a34a"
                : "#dc2626"
              : "#9ca3af"
          }
          icon={isBalanced ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          loading={summaryLoading}
          sub={
            summary && !isBalanced
              ? `Зөрүү: ${fmt(Math.abs(bankVsSystemDiff))}`
              : undefined
          }
        />
      </div>

      {/* POS Reconciliation detail panel */}
      <ReconciliationDetail summary={summary} loading={summaryLoading} />

      {/* Tabs */}
      <Card styles={{ body: { padding: "8px 12px" } }}>
        <Tabs items={tabItems} size="small" />
      </Card>
    </div>
  );
};

export default Reconciliation;
